import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";

// Thunks
export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async ({ organizationId, isDeleted = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/project?isDeleted=${isDeleted}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  "projects/fetchProjectById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/project/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createProject = createAsyncThunk(
  "projects/createProject",
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/api/v1/project",
        projectData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          transformRequest: [(data) => data], // Prevent axios from transforming FormData
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createVersion = createAsyncThunk(
  "projects/createVersion",
  async ({ projectId, versionData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/project/${projectId}/version`,
        versionData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchVersion = createAsyncThunk(
  "projects/fetchVersion",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/version/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteVersion = createAsyncThunk(
  "projects/deleteVersion",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/v1/version/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchVersionComments = createAsyncThunk(
  "projects/fetchVersionComments",
  async ({ versionId, isDeleted = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/version/${versionId}/comment?isDeleted=${isDeleted}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCommentReplies = createAsyncThunk(
  "projects/fetchCommentReplies",
  async (commentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/comment/${commentId}/replies`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchNotificationsSettings = createAsyncThunk(
  "app/fetchNotificationsSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/notification-preference`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateNotificationSetting = createAsyncThunk(
  "app/updateNotificationSetting",
  async ({ eventTypeId, channel, enabled }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/notification-preference/${eventTypeId}?channel=${channel}`,
        {
          enabled,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createVersionComment = createAsyncThunk(
  "projects/createVersionComments",
  async ({ versionId, commentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/version/${versionId}/comment`,
        commentData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteVersionComment = createAsyncThunk(
  "projects/deleteVersionComments",
  async ({ versionId, commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/comment/${commentId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const forceDeleteVersionComment = createAsyncThunk(
  "projects/deleteVersionComments",
  async ({ versionId, commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/comment/${commentId}/force`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async ({ id, projectData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/project/${id}`,
        projectData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          transformRequest: [(data) => data],
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/v1/project/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "app/fetchNotifications",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/notification`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const readAllNotifications = createAsyncThunk(
  "app/readAllNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/notification/read-all`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const setIsAppLoading = (value = false) => ({
  type: "SET_IS_APP_LOADING",
  payload: value,
});

export const setIsMainLoaded = (value = false) => ({
  type: "SET_IS_MAIN_LOADED",
  payload: value,
});

export const setRedirectingFromSharing = (value = false) => ({
  type: "SET_REDIRECTING_FROM_SHARING",
  payload: value,
});

export const setCurrentUploadingVideosInfo = (payload) => ({
  type: "SET_CURRENT_UPLOADING_VIDEO_INFO",
  payload,
});

const appSlice = createSlice({
  name: "app",
  initialState: {
    isAppLoading: true,
    isMainLoaded: false,
    isRedirectFromSharing: false,
    notificationsArray: [],
    isUploadingVersionShown: false,
    currentUploadingVideosInfo: [],
    devInfo: {
      isDev: false,
      devToken:
        "eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICItdllNQmNpdE5mQ0NqS2tDd2pBOFBYRUU0N1I1UnFxXzVILWtOVDhXYmJRIn0.eyJleHAiOjE3NzQ3NjEzOTYsImlhdCI6MTc3NDcwNzM5NiwiYXV0aF90aW1lIjoxNzc0NDQyMjc5LCJqdGkiOiJjYmVmNDIxYS0yMTlhLTQ5YjYtYWMwNi02NjA4Y2M0NjljYWEiLCJpc3MiOiJodHRwczovL2F1dGguN2N1dC52aWRlby9yZWFsbXMvdGltZWxpbmVyIiwiYXVkIjoiYWNjb3VudCIsInN1YiI6ImQ0NzdmMWUwLTE3OTgtNDFjZi04MTQyLTRiNTU1Njc1ZmNiYSIsInR5cCI6IkJlYXJlciIsImF6cCI6InRpbWVsaW5lci1jbGllbnQtNHk3bmt3ejEzdmIwdXFzIiwic2Vzc2lvbl9zdGF0ZSI6IjFlNjkxOWFjLWM2ZDEtNDQwNi1iNDg5LTA2NTFhZWM1MzMxMSIsImFjciI6IjEiLCJhbGxvd2VkLW9yaWdpbnMiOlsiaHR0cHM6Ly9nby43Y3V0LnZpZGVvLyoiLCJodHRwOi8vbG9jYWxob3N0OjgwODAiLCJodHRwczovL2dvLnRpbWVsaW5lci52aWRlbyIsImh0dHBzOi8vdGltZWxpbmVhcHAub25yZW5kZXIuY29tIl0sInJlYWxtX2FjY2VzcyI6eyJyb2xlcyI6WyJkZWZhdWx0LXJvbGVzLXRpbWVsaW5lciIsIm9mZmxpbmVfYWNjZXNzIiwidW1hX2F1dGhvcml6YXRpb24iXX0sInJlc291cmNlX2FjY2VzcyI6eyJhY2NvdW50Ijp7InJvbGVzIjpbIm1hbmFnZS1hY2NvdW50IiwibWFuYWdlLWFjY291bnQtbGlua3MiLCJ2aWV3LXByb2ZpbGUiXX19LCJzY29wZSI6Im9wZW5pZCBvZmZsaW5lX2FjY2VzcyBlbWFpbCBwcm9maWxlIiwic2lkIjoiMWU2OTE5YWMtYzZkMS00NDA2LWI0ODktMDY1MWFlYzUzMzExIiwiZW1haWxfdmVyaWZpZWQiOnRydWUsIm5hbWUiOiLQmNC70YzRjyDQlNGD0YbQtdCyIiwicHJlZmVycmVkX3VzZXJuYW1lIjoiZWxpamFoLmR1dHNldkBnbWFpbC5jb20iLCJnaXZlbl9uYW1lIjoi0JjQu9GM0Y8iLCJmYW1pbHlfbmFtZSI6ItCU0YPRhtC10LIiLCJlbWFpbCI6ImVsaWphaC5kdXRzZXZAZ21haWwuY29tIn0.JT0luxqzTmiKhM_4mSsx7tU3JRamDBNgWlWQS6Hn-_xks6uphtNIR8mNjysVXxq1fNbvvPshiq8Ax66tSgczXHWJDlJ8KsUCuO37mRfWevcAB2fPiiLjKZHstTq6vZ2dsKICVmqQ64CCUfQgmKqwNdSubmHsJq7G6AjRnyXjTh0YWVcvA9ue8EiXv-NlxWpJ-8kr9LUzRDahN9iBWCBVPa63NxQc2sOFQCY1KGRmpfB4YbmGbiEocWEhGYSJlL8w3CgwD5TtITStw7_qJElwNiQzDMRDERKWDthrn1ynT3PywVwlDcfT027Fn1n0EqD46rf3hKcPIu4k5cIQJe8whA",
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase("SET_IS_APP_LOADING", (state, action) => {
        state.isAppLoading = action.payload;
      })
      .addCase("SET_CURRENT_UPLOADING_VIDEO_INFO", (state, action) => {
        state.currentUploadingVideosInfo = action.payload;
      })
      .addCase("SET_IS_MAIN_LOADED", (state, action) => {
        state.isMainLoaded = action.payload;
      })
      .addCase("SET_REDIRECTING_FROM_SHARING", (state, action) => {
        state.isRedirectFromSharing = action.payload;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsArray = action.payload || [];
      });
  },
});

export default appSlice.reducer;
